/** @file buildChannels: a clip split into per-node sorted key channels for a rig, memoised per (clip, rig). */
import type { Clip, ClipKey, NodeKey } from '@zilo/pose3d-format/types/clip';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import type { ClipChannels, Key, NodeChannels } from '@zilo/pose3d-pose/sample/channelTypes';

const MEMO = new WeakMap<Clip, WeakMap<Rig, ClipChannels>>();

const emptyNode = (): NodeChannels => ({ rot: [], pos: [], scale: [], parent: [], hidden: [], rotToggle: [] });

function addKey(ch: NodeChannels, key: ClipKey, nk: NodeKey, rig: Rig): void {
  const base = { frame: key.frame, ease: nk.ease ?? key.ease ?? 'linear', hold: nk.hold ?? key.hold ?? 1 };
  const push = <T>(list: Key<T>[], value: T) => { list.push({ ...base, value }); };
  if (nk.rot !== undefined) push(ch.rot, quatFromYawPitchRoll(nk.rot[0], nk.rot[1], nk.rot[2]));
  if (nk.pos !== undefined) push(ch.pos, [nk.pos[0], nk.pos[1], nk.pos[2]] as const);
  if (nk.scale !== undefined) push(ch.scale, nk.scale);
  if (nk.parent === null) push(ch.parent, -1);
  else if (nk.parent !== undefined) {
    const p = rig.index.get(nk.parent);
    if (p !== undefined) push(ch.parent, p);
  }
  if (nk.hidden !== undefined) push(ch.hidden, nk.hidden);
  if (nk.rotToggle !== undefined) push(ch.rotToggle, nk.rotToggle);
}

function build(clip: Clip, rig: Rig): ClipChannels {
  const nodes = rig.names.map(emptyNode);
  const unknown = new Set<string>();
  for (const key of [...clip.keys].sort((a, b) => a.frame - b.frame)) {
    for (const [name, nk] of Object.entries(key.nodes)) {
      const i = rig.index.get(name);
      const ch = i === undefined ? undefined : nodes[i];
      if (ch === undefined) unknown.add(name);
      else addKey(ch, key, nk, rig);
    }
  }
  const loopLen = clip.frames - clip.loopStart;
  const half = clip.mirrorHalf && loopLen > 1 ? Math.floor(loopLen / 2) : 0;
  return { clip, rig, nodes, loopLen, half, unknownNodes: [...unknown] };
}

/**
 * Channels of `clip` on `rig` (17.6): keys sorted by frame, per node `ease = nodeKey.ease ?? key.ease ?? 'linear'`,
 * `hold = nodeKey.hold ?? key.hold ?? 1`, rotations as quaternions, parent names as indices (`null` -> -1, unknown
 * names dropped); nodes the rig lacks go to `unknownNodes`. Memoised per (clip, rig) object.
 */
export function buildChannels(clip: Clip, rig: Rig): ClipChannels {
  let byRig = MEMO.get(clip);
  if (byRig === undefined) {
    byRig = new WeakMap();
    MEMO.set(clip, byRig);
  }
  let channels = byRig.get(rig);
  if (channels === undefined) {
    channels = build(clip, rig);
    byRig.set(rig, channels);
  }
  return channels;
}
